import { useBuilderStore } from '@/stores/builderStore';
import { Input, Label, Textarea, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Switch, Tabs, TabsContent, TabsList, TabsTrigger, ImageUpload, ListEditor, type ListField } from '@mawkaa/ui';
import { getBlockDefinition } from '@mawkaa/blocks';
import type { Block } from '@mawkaa/types';
import { uploadFile } from '@/lib/api';

export function PropertiesPanel() {
  const { blocks, selectedBlockId, updateBlock } = useBuilderStore();

  const selectedBlock = blocks.find((b) => b.id === selectedBlockId);
  if (!selectedBlock) return null;

  const blockDef = getBlockDefinition(selectedBlock.type);

  const handleContentChange = (key: string, value: any) => {
    updateBlock(selectedBlock.id, {
      content: { ...selectedBlock.content, [key]: value },
    });
  };

  const handleSettingsChange = (key: string, value: any) => {
    updateBlock(selectedBlock.id, {
      settings: { ...selectedBlock.settings, [key]: value },
    });
  };

  return (
    <div className="p-4">
      <div className="mb-4">
        <h3 className="font-medium">{blockDef?.name || selectedBlock.type}</h3>
        <p className="text-sm text-muted-foreground">{blockDef?.description}</p>
      </div>

      <Tabs defaultValue="content">
        <TabsList className="w-full">
          <TabsTrigger value="content" className="flex-1">Contenu</TabsTrigger>
          <TabsTrigger value="style" className="flex-1">Style</TabsTrigger>
        </TabsList>

        <TabsContent value="content" className="space-y-4 mt-4">
          {renderContentFields(selectedBlock, handleContentChange)}
        </TabsContent>

        <TabsContent value="style" className="space-y-4 mt-4">
          {/* Background color */}
          <div className="space-y-2">
            <Label>Couleur de fond</Label>
            <Input
              type="color"
              value={selectedBlock.settings.backgroundColor || '#ffffff'}
              onChange={(e) => handleSettingsChange('backgroundColor', e.target.value)}
              className="h-10 w-full"
            />
          </div>

          {/* Padding */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Padding haut</Label>
              <Input
                type="number"
                value={selectedBlock.settings.paddingTop || 0}
                onChange={(e) => handleSettingsChange('paddingTop', parseInt(e.target.value))}
              />
            </div>
            <div className="space-y-2">
              <Label>Padding bas</Label>
              <Input
                type="number"
                value={selectedBlock.settings.paddingBottom || 0}
                onChange={(e) => handleSettingsChange('paddingBottom', parseInt(e.target.value))}
              />
            </div>
          </div>

          {/* Hidden toggle */}
          <div className="flex items-center justify-between">
            <Label>Masquer ce bloc</Label>
            <Switch
              checked={selectedBlock.settings.hidden || false}
              onCheckedChange={(checked) => handleSettingsChange('hidden', checked)}
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Field definitions for list editors
const featureFields: ListField[] = [
  { key: 'icon', label: 'Icône', type: 'text', placeholder: 'star, heart, zap...' },
  { key: 'title', label: 'Titre', type: 'text', placeholder: 'Titre de la fonctionnalité' },
  { key: 'description', label: 'Description', type: 'textarea', placeholder: 'Description...' },
];

const serviceFields: ListField[] = [
  { key: 'image', label: 'Image', type: 'image' },
  { key: 'title', label: 'Titre', type: 'text', placeholder: 'Nom du service' },
  { key: 'description', label: 'Description', type: 'textarea' },
  { key: 'price', label: 'Prix', type: 'text', placeholder: '50 TND' },
];

const testimonialFields: ListField[] = [
  { key: 'avatar', label: 'Photo', type: 'image' },
  { key: 'name', label: 'Nom', type: 'text', placeholder: 'Jean Dupont' },
  { key: 'role', label: 'Rôle', type: 'text', placeholder: 'CEO, Entreprise' },
  { key: 'content', label: 'Témoignage', type: 'textarea', placeholder: 'Ce que le client dit...' },
  { key: 'rating', label: 'Note (1-5)', type: 'number' },
];

const faqFields: ListField[] = [
  { key: 'question', label: 'Question', type: 'text', placeholder: 'Quelle est votre question ?' },
  { key: 'answer', label: 'Réponse', type: 'textarea', placeholder: 'La réponse à la question...' },
];

const galleryFields: ListField[] = [
  { key: 'src', label: 'Image', type: 'image' },
  { key: 'alt', label: 'Texte alternatif', type: 'text', placeholder: 'Description de l\'image' },
  { key: 'caption', label: 'Légende', type: 'text' },
];

const teamFields: ListField[] = [
  { key: 'image', label: 'Photo', type: 'image' },
  { key: 'name', label: 'Nom', type: 'text', placeholder: 'Prénom Nom' },
  { key: 'role', label: 'Poste', type: 'text', placeholder: 'Directeur, Designer...' },
  { key: 'bio', label: 'Bio', type: 'textarea' },
];

const pricingFields: ListField[] = [
  { key: 'name', label: 'Nom du plan', type: 'text', placeholder: 'Starter, Pro...' },
  { key: 'price', label: 'Prix', type: 'text', placeholder: '29 TND' },
  { key: 'period', label: 'Période', type: 'text', placeholder: '/mois' },
  { key: 'description', label: 'Description', type: 'textarea' },
  { key: 'buttonText', label: 'Texte du bouton', type: 'text', placeholder: 'Commencer' },
];

const menuCategoryFields: ListField[] = [
  { key: 'name', label: 'Nom de catégorie', type: 'text', placeholder: 'Entrées, Plats...' },
  { key: 'description', label: 'Description', type: 'textarea' },
];

const menuItemFields: ListField[] = [
  { key: 'name', label: 'Nom du plat', type: 'text', placeholder: 'Salade César' },
  { key: 'description', label: 'Description', type: 'textarea' },
  { key: 'price', label: 'Prix', type: 'text', placeholder: '25 TND' },
  { key: 'image', label: 'Photo', type: 'image' },
];

const productFields: ListField[] = [
  { key: 'image', label: 'Image', type: 'image' },
  { key: 'name', label: 'Nom', type: 'text', placeholder: 'Nom du produit' },
  { key: 'description', label: 'Description', type: 'textarea' },
  { key: 'price', label: 'Prix', type: 'text', placeholder: '99 TND' },
  { key: 'badge', label: 'Badge', type: 'text', placeholder: 'Nouveau, Promo...' },
  { key: 'buttonText', label: 'Texte du bouton', type: 'text', placeholder: 'Acheter' },
];

const hoursFields: ListField[] = [
  { key: 'day', label: 'Jour', type: 'text', placeholder: 'Lundi' },
  { key: 'open', label: 'Ouverture', type: 'text', placeholder: '09:00' },
  { key: 'close', label: 'Fermeture', type: 'text', placeholder: '18:00' },
];

const navItemFields: ListField[] = [
  { key: 'label', label: 'Texte', type: 'text', placeholder: 'Accueil' },
  { key: 'url', label: 'Lien', type: 'text', placeholder: '#section ou /page' },
];

const socialLinkFields: ListField[] = [
  { key: 'platform', label: 'Plateforme', type: 'text', placeholder: 'facebook, instagram...' },
  { key: 'url', label: 'URL', type: 'url', placeholder: 'https://...' },
];

const ctaButtonFields: ListField[] = [
  { key: 'text', label: 'Texte', type: 'text', placeholder: 'En savoir plus' },
  { key: 'url', label: 'Lien', type: 'text', placeholder: '#contact' },
];

function renderContentFields(block: Block, onChange: (key: string, value: any) => void) {
  const content = block.content as Record<string, any>;

  switch (block.type) {
    case 'hero':
      return (
        <>
          <div className="space-y-2">
            <Label>Titre</Label>
            <Input
              value={content.title || ''}
              onChange={(e) => onChange('title', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Sous-titre</Label>
            <Input
              value={content.subtitle || ''}
              onChange={(e) => onChange('subtitle', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={content.description || ''}
              onChange={(e) => onChange('description', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Image de fond</Label>
            <ImageUpload
              value={content.backgroundImage || ''}
              onChange={(url) => onChange('backgroundImage', url)}
              onUpload={uploadFile}
              aspectRatio="16:9"
            />
          </div>
          <div className="space-y-2">
            <Label>Boutons d'action</Label>
            <ListEditor
              items={content.ctaButtons || []}
              onChange={(items) => onChange('ctaButtons', items)}
              fields={ctaButtonFields}
              itemLabel="Bouton"
              maxItems={3}
            />
          </div>
          <div className="space-y-2">
            <Label>Style</Label>
            <Select
              value={content.style || 'centered'}
              onValueChange={(value) => onChange('style', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="centered">Centré</SelectItem>
                <SelectItem value="left">Gauche</SelectItem>
                <SelectItem value="right">Droite</SelectItem>
                <SelectItem value="split">Divisé</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      );

    case 'image':
      return (
        <>
          <div className="space-y-2">
            <Label>Image</Label>
            <ImageUpload
              value={content.src || ''}
              onChange={(url) => onChange('src', url)}
              onUpload={uploadFile}
              aspectRatio={content.aspectRatio || 'auto'}
            />
          </div>
          <div className="space-y-2">
            <Label>Texte alternatif</Label>
            <Input
              value={content.alt || ''}
              onChange={(e) => onChange('alt', e.target.value)}
              placeholder="Description de l'image"
            />
          </div>
          <div className="space-y-2">
            <Label>Légende</Label>
            <Input
              value={content.caption || ''}
              onChange={(e) => onChange('caption', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Lien</Label>
            <Input
              value={content.link || ''}
              onChange={(e) => onChange('link', e.target.value)}
              placeholder="https://..."
            />
          </div>
          <div className="space-y-2">
            <Label>Format</Label>
            <Select
              value={content.aspectRatio || 'auto'}
              onValueChange={(value) => onChange('aspectRatio', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Automatique</SelectItem>
                <SelectItem value="1:1">Carré (1:1)</SelectItem>
                <SelectItem value="4:3">Standard (4:3)</SelectItem>
                <SelectItem value="16:9">Vidéo (16:9)</SelectItem>
                <SelectItem value="21:9">Cinéma (21:9)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      );

    case 'text':
      return (
        <>
          <div className="space-y-2">
            <Label>Contenu</Label>
            <Textarea
              value={content.content || ''}
              onChange={(e) => onChange('content', e.target.value)}
              rows={6}
            />
          </div>
          <div className="space-y-2">
            <Label>Alignement</Label>
            <Select
              value={content.alignment || 'left'}
              onValueChange={(value) => onChange('alignment', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="left">Gauche</SelectItem>
                <SelectItem value="center">Centre</SelectItem>
                <SelectItem value="right">Droite</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      );

    case 'cta':
      return (
        <>
          <div className="space-y-2">
            <Label>Titre</Label>
            <Input
              value={content.title || ''}
              onChange={(e) => onChange('title', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={content.description || ''}
              onChange={(e) => onChange('description', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Image de fond</Label>
            <ImageUpload
              value={content.backgroundImage || ''}
              onChange={(url) => onChange('backgroundImage', url)}
              onUpload={uploadFile}
              aspectRatio="16:9"
            />
          </div>
          <div className="space-y-2">
            <Label>Boutons</Label>
            <ListEditor
              items={content.buttons || []}
              onChange={(items) => onChange('buttons', items)}
              fields={ctaButtonFields}
              itemLabel="Bouton"
              maxItems={2}
            />
          </div>
        </>
      );

    case 'contact':
      return (
        <>
          <div className="space-y-2">
            <Label>Titre</Label>
            <Input
              value={content.title || ''}
              onChange={(e) => onChange('title', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Sous-titre</Label>
            <Input
              value={content.subtitle || ''}
              onChange={(e) => onChange('subtitle', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Email</Label>
            <Input
              value={content.email || ''}
              onChange={(e) => onChange('email', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Téléphone</Label>
            <Input
              value={content.phone || ''}
              onChange={(e) => onChange('phone', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>WhatsApp</Label>
            <Input
              value={content.whatsapp || ''}
              onChange={(e) => onChange('whatsapp', e.target.value)}
              placeholder="+216..."
            />
          </div>
          <div className="space-y-2">
            <Label>Adresse</Label>
            <Textarea
              value={content.address || ''}
              onChange={(e) => onChange('address', e.target.value)}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label>Afficher le formulaire</Label>
            <Switch
              checked={content.showForm !== false}
              onCheckedChange={(checked) => onChange('showForm', checked)}
            />
          </div>
        </>
      );

    case 'header':
      return (
        <>
          <div className="space-y-2">
            <Label>Logo</Label>
            <ImageUpload
              value={content.logo || ''}
              onChange={(url) => onChange('logo', url)}
              onUpload={uploadFile}
              aspectRatio="auto"
              placeholder="Télécharger votre logo"
            />
          </div>
          <div className="space-y-2">
            <Label>Texte du logo</Label>
            <Input
              value={content.logoText || ''}
              onChange={(e) => onChange('logoText', e.target.value)}
              placeholder="Nom de votre entreprise"
            />
          </div>
          <div className="space-y-2">
            <Label>Navigation</Label>
            <ListEditor
              items={content.navigation || content.menuItems || []}
              onChange={(items) => onChange('navigation', items)}
              fields={navItemFields}
              itemLabel="Lien"
              maxItems={8}
            />
          </div>
          <div className="space-y-2">
            <Label>Style</Label>
            <Select
              value={content.style || 'simple'}
              onValueChange={(value) => onChange('style', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="simple">Simple</SelectItem>
                <SelectItem value="centered">Centré</SelectItem>
                <SelectItem value="split">Divisé</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between">
            <Label>Afficher le bouton CTA</Label>
            <Switch
              checked={content.showCta || false}
              onCheckedChange={(checked) => onChange('showCta', checked)}
            />
          </div>
          {content.showCta && (
            <>
              <div className="space-y-2">
                <Label>Texte du bouton</Label>
                <Input
                  value={content.ctaText || ''}
                  onChange={(e) => onChange('ctaText', e.target.value)}
                  placeholder="Contactez-nous"
                />
              </div>
              <div className="space-y-2">
                <Label>Lien du bouton</Label>
                <Input
                  value={content.ctaLink || ''}
                  onChange={(e) => onChange('ctaLink', e.target.value)}
                  placeholder="#contact"
                />
              </div>
            </>
          )}
        </>
      );

    case 'footer':
      return (
        <>
          <div className="space-y-2">
            <Label>Logo</Label>
            <ImageUpload
              value={content.logo || ''}
              onChange={(url) => onChange('logo', url)}
              onUpload={uploadFile}
              aspectRatio="auto"
            />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={content.description || ''}
              onChange={(e) => onChange('description', e.target.value)}
              placeholder="Une brève description de votre entreprise"
            />
          </div>
          <div className="space-y-2">
            <Label>Réseaux sociaux</Label>
            <ListEditor
              items={content.socialLinks || []}
              onChange={(items) => onChange('socialLinks', items)}
              fields={socialLinkFields}
              itemLabel="Réseau"
              maxItems={6}
            />
          </div>
          <div className="space-y-2">
            <Label>Copyright</Label>
            <Input
              value={content.copyright || ''}
              onChange={(e) => onChange('copyright', e.target.value)}
              placeholder="© 2024 Votre Entreprise"
            />
          </div>
          <div className="space-y-2">
            <Label>Style</Label>
            <Select
              value={content.style || 'simple'}
              onValueChange={(value) => onChange('style', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="simple">Simple</SelectItem>
                <SelectItem value="multi-column">Multi-colonnes</SelectItem>
                <SelectItem value="centered">Centré</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      );

    case 'features':
      return (
        <>
          <div className="space-y-2">
            <Label>Titre</Label>
            <Input
              value={content.title || ''}
              onChange={(e) => onChange('title', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Sous-titre</Label>
            <Input
              value={content.subtitle || ''}
              onChange={(e) => onChange('subtitle', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Fonctionnalités</Label>
            <ListEditor
              items={content.features || content.items || []}
              onChange={(items) => onChange('features', items)}
              fields={featureFields}
              onUpload={uploadFile}
              itemLabel="Fonctionnalité"
              maxItems={12}
            />
          </div>
          <div className="space-y-2">
            <Label>Colonnes</Label>
            <Select
              value={String(content.columns || 3)}
              onValueChange={(value) => onChange('columns', parseInt(value))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2">2 colonnes</SelectItem>
                <SelectItem value="3">3 colonnes</SelectItem>
                <SelectItem value="4">4 colonnes</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Style</Label>
            <Select
              value={content.style || 'cards'}
              onValueChange={(value) => onChange('style', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cards">Cartes</SelectItem>
                <SelectItem value="icons">Icônes</SelectItem>
                <SelectItem value="minimal">Minimal</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      );

    case 'services':
      return (
        <>
          <div className="space-y-2">
            <Label>Titre</Label>
            <Input
              value={content.title || ''}
              onChange={(e) => onChange('title', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Sous-titre</Label>
            <Input
              value={content.subtitle || ''}
              onChange={(e) => onChange('subtitle', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Services</Label>
            <ListEditor
              items={content.services || content.items || []}
              onChange={(items) => onChange('services', items)}
              fields={serviceFields}
              onUpload={uploadFile}
              itemLabel="Service"
              maxItems={12}
            />
          </div>
          <div className="space-y-2">
            <Label>Colonnes</Label>
            <Select
              value={String(content.columns || 3)}
              onValueChange={(value) => onChange('columns', parseInt(value))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2">2 colonnes</SelectItem>
                <SelectItem value="3">3 colonnes</SelectItem>
                <SelectItem value="4">4 colonnes</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Style</Label>
            <Select
              value={content.style || 'cards'}
              onValueChange={(value) => onChange('style', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cards">Cartes</SelectItem>
                <SelectItem value="list">Liste</SelectItem>
                <SelectItem value="grid">Grille</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      );

    case 'testimonials':
      return (
        <>
          <div className="space-y-2">
            <Label>Titre</Label>
            <Input
              value={content.title || ''}
              onChange={(e) => onChange('title', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Témoignages</Label>
            <ListEditor
              items={content.testimonials || content.items || []}
              onChange={(items) => onChange('testimonials', items)}
              fields={testimonialFields}
              onUpload={uploadFile}
              itemLabel="Témoignage"
              maxItems={10}
            />
          </div>
          <div className="space-y-2">
            <Label>Style</Label>
            <Select
              value={content.style || 'cards'}
              onValueChange={(value) => onChange('style', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cards">Cartes</SelectItem>
                <SelectItem value="carousel">Carrousel</SelectItem>
                <SelectItem value="minimal">Minimal</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      );

    case 'faq':
      return (
        <>
          <div className="space-y-2">
            <Label>Titre</Label>
            <Input
              value={content.title || ''}
              onChange={(e) => onChange('title', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Questions / Réponses</Label>
            <ListEditor
              items={content.faqs || content.items || []}
              onChange={(items) => onChange('faqs', items)}
              fields={faqFields}
              itemLabel="Question"
              maxItems={20}
            />
          </div>
          <div className="space-y-2">
            <Label>Style</Label>
            <Select
              value={content.style || 'accordion'}
              onValueChange={(value) => onChange('style', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="accordion">Accordéon</SelectItem>
                <SelectItem value="list">Liste</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      );

    case 'gallery':
      return (
        <>
          <div className="space-y-2">
            <Label>Titre</Label>
            <Input
              value={content.title || ''}
              onChange={(e) => onChange('title', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Images</Label>
            <ListEditor
              items={content.images || []}
              onChange={(items) => onChange('images', items)}
              fields={galleryFields}
              onUpload={uploadFile}
              itemLabel="Image"
              maxItems={20}
            />
          </div>
          <div className="space-y-2">
            <Label>Colonnes</Label>
            <Select
              value={String(content.columns || 3)}
              onValueChange={(value) => onChange('columns', parseInt(value))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2">2 colonnes</SelectItem>
                <SelectItem value="3">3 colonnes</SelectItem>
                <SelectItem value="4">4 colonnes</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Style</Label>
            <Select
              value={content.style || 'grid'}
              onValueChange={(value) => onChange('style', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="grid">Grille</SelectItem>
                <SelectItem value="masonry">Masonry</SelectItem>
                <SelectItem value="carousel">Carrousel</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between">
            <Label>Lightbox</Label>
            <Switch
              checked={content.lightbox !== false}
              onCheckedChange={(checked) => onChange('lightbox', checked)}
            />
          </div>
        </>
      );

    case 'team':
      return (
        <>
          <div className="space-y-2">
            <Label>Titre</Label>
            <Input
              value={content.title || ''}
              onChange={(e) => onChange('title', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Sous-titre</Label>
            <Input
              value={content.subtitle || ''}
              onChange={(e) => onChange('subtitle', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Membres de l'équipe</Label>
            <ListEditor
              items={content.members || []}
              onChange={(items) => onChange('members', items)}
              fields={teamFields}
              onUpload={uploadFile}
              itemLabel="Membre"
              maxItems={12}
            />
          </div>
          <div className="space-y-2">
            <Label>Colonnes</Label>
            <Select
              value={String(content.columns || 3)}
              onValueChange={(value) => onChange('columns', parseInt(value))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2">2 colonnes</SelectItem>
                <SelectItem value="3">3 colonnes</SelectItem>
                <SelectItem value="4">4 colonnes</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      );

    case 'pricing':
      return (
        <>
          <div className="space-y-2">
            <Label>Titre</Label>
            <Input
              value={content.title || ''}
              onChange={(e) => onChange('title', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Sous-titre</Label>
            <Input
              value={content.subtitle || ''}
              onChange={(e) => onChange('subtitle', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Plans tarifaires</Label>
            <ListEditor
              items={content.plans || []}
              onChange={(items) => onChange('plans', items)}
              fields={pricingFields}
              itemLabel="Plan"
              maxItems={4}
            />
          </div>
          <div className="space-y-2">
            <Label>Style</Label>
            <Select
              value={content.style || 'cards'}
              onValueChange={(value) => onChange('style', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cards">Cartes</SelectItem>
                <SelectItem value="table">Tableau</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      );

    case 'menu':
      return (
        <>
          <div className="space-y-2">
            <Label>Titre</Label>
            <Input
              value={content.title || ''}
              onChange={(e) => onChange('title', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Catégories</Label>
            <ListEditor
              items={content.categories || []}
              onChange={(items) => onChange('categories', items)}
              fields={menuCategoryFields}
              itemLabel="Catégorie"
              maxItems={10}
            />
          </div>
          <div className="space-y-2">
            <Label>Devise</Label>
            <Input
              value={content.currency || 'TND'}
              onChange={(e) => onChange('currency', e.target.value)}
              placeholder="TND, EUR, USD..."
            />
          </div>
          <div className="flex items-center justify-between">
            <Label>Afficher les prix</Label>
            <Switch
              checked={content.showPrices !== false}
              onCheckedChange={(checked) => onChange('showPrices', checked)}
            />
          </div>
          <div className="space-y-2">
            <Label>Style</Label>
            <Select
              value={content.style || 'simple'}
              onValueChange={(value) => onChange('style', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="simple">Simple</SelectItem>
                <SelectItem value="cards">Cartes</SelectItem>
                <SelectItem value="detailed">Détaillé</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      );

    case 'products':
      return (
        <>
          <div className="space-y-2">
            <Label>Titre</Label>
            <Input
              value={content.title || ''}
              onChange={(e) => onChange('title', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Sous-titre</Label>
            <Input
              value={content.subtitle || ''}
              onChange={(e) => onChange('subtitle', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Produits</Label>
            <ListEditor
              items={content.products || []}
              onChange={(items) => onChange('products', items)}
              fields={productFields}
              onUpload={uploadFile}
              itemLabel="Produit"
              maxItems={20}
            />
          </div>
          <div className="space-y-2">
            <Label>Colonnes</Label>
            <Select
              value={String(content.columns || 3)}
              onValueChange={(value) => onChange('columns', parseInt(value))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2">2 colonnes</SelectItem>
                <SelectItem value="3">3 colonnes</SelectItem>
                <SelectItem value="4">4 colonnes</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Devise</Label>
            <Input
              value={content.currency || 'TND'}
              onChange={(e) => onChange('currency', e.target.value)}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label>Afficher les prix</Label>
            <Switch
              checked={content.showPrices !== false}
              onCheckedChange={(checked) => onChange('showPrices', checked)}
            />
          </div>
        </>
      );

    case 'hours':
      return (
        <>
          <div className="space-y-2">
            <Label>Titre</Label>
            <Input
              value={content.title || ''}
              onChange={(e) => onChange('title', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Horaires</Label>
            <ListEditor
              items={content.schedule || []}
              onChange={(items) => onChange('schedule', items)}
              fields={hoursFields}
              itemLabel="Jour"
              maxItems={7}
              defaultItem={{ day: '', open: '09:00', close: '18:00' }}
            />
          </div>
          <div className="space-y-2">
            <Label>Note</Label>
            <Textarea
              value={content.note || ''}
              onChange={(e) => onChange('note', e.target.value)}
              placeholder="Fermé les jours fériés..."
            />
          </div>
          <div className="flex items-center justify-between">
            <Label>Afficher le statut actuel</Label>
            <Switch
              checked={content.showCurrentStatus || false}
              onCheckedChange={(checked) => onChange('showCurrentStatus', checked)}
            />
          </div>
        </>
      );

    case 'social':
      return (
        <>
          <div className="space-y-2">
            <Label>Titre</Label>
            <Input
              value={content.title || ''}
              onChange={(e) => onChange('title', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Sous-titre</Label>
            <Input
              value={content.subtitle || ''}
              onChange={(e) => onChange('subtitle', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Liens sociaux</Label>
            <ListEditor
              items={content.links || []}
              onChange={(items) => onChange('links', items)}
              fields={socialLinkFields}
              itemLabel="Réseau"
              maxItems={10}
            />
          </div>
          <div className="space-y-2">
            <Label>Style</Label>
            <Select
              value={content.style || 'icons'}
              onValueChange={(value) => onChange('style', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="icons">Icônes</SelectItem>
                <SelectItem value="buttons">Boutons</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      );

    case 'newsletter':
      return (
        <>
          <div className="space-y-2">
            <Label>Titre</Label>
            <Input
              value={content.title || ''}
              onChange={(e) => onChange('title', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={content.description || ''}
              onChange={(e) => onChange('description', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Placeholder</Label>
            <Input
              value={content.placeholder || ''}
              onChange={(e) => onChange('placeholder', e.target.value)}
              placeholder="Votre email..."
            />
          </div>
          <div className="space-y-2">
            <Label>Texte du bouton</Label>
            <Input
              value={content.buttonText || ''}
              onChange={(e) => onChange('buttonText', e.target.value)}
              placeholder="S'inscrire"
            />
          </div>
          <div className="space-y-2">
            <Label>Message de succès</Label>
            <Input
              value={content.successMessage || ''}
              onChange={(e) => onChange('successMessage', e.target.value)}
              placeholder="Merci pour votre inscription !"
            />
          </div>
          <div className="space-y-2">
            <Label>Style</Label>
            <Select
              value={content.style || 'inline'}
              onValueChange={(value) => onChange('style', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="inline">En ligne</SelectItem>
                <SelectItem value="stacked">Empilé</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      );

    case 'booking':
      return (
        <>
          <div className="space-y-2">
            <Label>Titre</Label>
            <Input
              value={content.title || ''}
              onChange={(e) => onChange('title', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={content.description || ''}
              onChange={(e) => onChange('description', e.target.value)}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label>Afficher la date</Label>
            <Switch
              checked={content.showDate !== false}
              onCheckedChange={(checked) => onChange('showDate', checked)}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label>Afficher l'heure</Label>
            <Switch
              checked={content.showTime !== false}
              onCheckedChange={(checked) => onChange('showTime', checked)}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label>Afficher le nombre de personnes</Label>
            <Switch
              checked={content.showGuests || false}
              onCheckedChange={(checked) => onChange('showGuests', checked)}
            />
          </div>
        </>
      );

    case 'map':
      return (
        <>
          <div className="space-y-2">
            <Label>Titre</Label>
            <Input
              value={content.title || ''}
              onChange={(e) => onChange('title', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Adresse</Label>
            <Textarea
              value={content.address || ''}
              onChange={(e) => onChange('address', e.target.value)}
              placeholder="123 Rue Example, Tunis"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Latitude</Label>
              <Input
                type="number"
                step="any"
                value={content.lat || ''}
                onChange={(e) => onChange('lat', parseFloat(e.target.value))}
                placeholder="36.8065"
              />
            </div>
            <div className="space-y-2">
              <Label>Longitude</Label>
              <Input
                type="number"
                step="any"
                value={content.lng || ''}
                onChange={(e) => onChange('lng', parseFloat(e.target.value))}
                placeholder="10.1815"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Zoom</Label>
            <Input
              type="number"
              value={content.zoom || 15}
              onChange={(e) => onChange('zoom', parseInt(e.target.value))}
              min={1}
              max={20}
            />
          </div>
          <div className="space-y-2">
            <Label>Hauteur (px)</Label>
            <Input
              type="number"
              value={content.height || 400}
              onChange={(e) => onChange('height', parseInt(e.target.value))}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label>Afficher le marqueur</Label>
            <Switch
              checked={content.showMarker !== false}
              onCheckedChange={(checked) => onChange('showMarker', checked)}
            />
          </div>
        </>
      );

    case 'video':
      return (
        <>
          <div className="space-y-2">
            <Label>Titre</Label>
            <Input
              value={content.title || ''}
              onChange={(e) => onChange('title', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>URL de la vidéo</Label>
            <Input
              value={content.url || ''}
              onChange={(e) => onChange('url', e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
            />
          </div>
          <div className="space-y-2">
            <Label>Source</Label>
            <Select
              value={content.provider || 'youtube'}
              onValueChange={(value) => onChange('provider', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="youtube">YouTube</SelectItem>
                <SelectItem value="vimeo">Vimeo</SelectItem>
                <SelectItem value="direct">Lien direct</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between">
            <Label>Lecture automatique</Label>
            <Switch
              checked={content.autoplay || false}
              onCheckedChange={(checked) => onChange('autoplay', checked)}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label>Boucle</Label>
            <Switch
              checked={content.loop || false}
              onCheckedChange={(checked) => onChange('loop', checked)}
            />
          </div>
        </>
      );

    case 'spacer':
      return (
        <>
          <div className="space-y-2">
            <Label>Hauteur (px)</Label>
            <Input
              type="number"
              value={content.height || 40}
              onChange={(e) => onChange('height', parseInt(e.target.value))}
            />
          </div>
          <div className="space-y-2">
            <Label>Hauteur mobile (px)</Label>
            <Input
              type="number"
              value={content.mobileHeight || content.height || 40}
              onChange={(e) => onChange('mobileHeight', parseInt(e.target.value))}
            />
          </div>
        </>
      );

    case 'divider':
      return (
        <>
          <div className="space-y-2">
            <Label>Style</Label>
            <Select
              value={content.style || 'line'}
              onValueChange={(value) => onChange('style', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="line">Ligne</SelectItem>
                <SelectItem value="dashed">Tirets</SelectItem>
                <SelectItem value="dotted">Points</SelectItem>
                <SelectItem value="gradient">Dégradé</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Couleur</Label>
            <Input
              type="color"
              value={content.color || '#e5e7eb'}
              onChange={(e) => onChange('color', e.target.value)}
              className="h-10 w-full"
            />
          </div>
          <div className="space-y-2">
            <Label>Largeur</Label>
            <Select
              value={content.width || 'full'}
              onValueChange={(value) => onChange('width', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="full">Pleine largeur</SelectItem>
                <SelectItem value="medium">Moyenne</SelectItem>
                <SelectItem value="small">Petite</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      );

    case 'html':
      return (
        <>
          <div className="space-y-2">
            <Label>Code HTML</Label>
            <Textarea
              value={content.code || ''}
              onChange={(e) => onChange('code', e.target.value)}
              rows={10}
              placeholder="<div>Votre code HTML ici...</div>"
              className="font-mono text-sm"
            />
          </div>
          <div className="flex items-center justify-between">
            <Label>Mode sandbox (sécurisé)</Label>
            <Switch
              checked={content.sandboxed !== false}
              onCheckedChange={(checked) => onChange('sandboxed', checked)}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Le mode sandbox isole le code HTML pour des raisons de sécurité.
          </p>
        </>
      );

    default:
      return (
        <p className="text-sm text-muted-foreground">
          Éditez les propriétés de ce bloc dans l'éditeur visuel.
        </p>
      );
  }
}
